import { Fragment, type ReactNode, useId } from 'react';
import classNames from 'classnames';

import type { ButtonSize } from '@/components/atoms/Buttons';
import {
    Menu,
    MenuContent,
    type MenuContentProps,
    MenuHeader,
    type MenuProps,
    MenuRadioItem,
    MenuTrigger,
    type MenuTriggerProps,
} from '@/components/molecules/Menu';

import styles from './SelectMenu.module.scss';

export interface SelectMenuOption<T extends string | number> {
    value: T;
    label: string;
    disabled?: boolean;
    /** Why a disabled option cannot be picked; shown on hover and read with the label. */
    disabledReason?: string;
}

export interface SelectMenuGroup<T extends string | number> {
    label: string;
    options: SelectMenuOption<T>[];
}

export interface SelectMenuProps<T extends string | number> {
    value: T;
    onChange: (next: T) => void;
    /** Pass `options` for a flat list or `groups` for headed sections. */
    options?: SelectMenuOption<T>[];
    groups?: SelectMenuGroup<T>[];
    /** Prefix on the trigger, which then reads "Label: value". */
    label?: string;
    /** Replaces the trigger text, e.g. "50 per page". */
    renderValue?: (option: SelectMenuOption<T> | undefined) => ReactNode;
    icon?: ReactNode;
    /** The chevron after the value; off for a trigger that reads as a link, such as the language. */
    caret?: boolean;
    variant?: MenuTriggerProps['variant'];
    size?: ButtonSize;
    fullWidth?: boolean;
    disabled?: boolean;
    align?: MenuContentProps['align'];
    /** Gap between the trigger and the popup. The menu default is 8. */
    offset?: MenuContentProps['offset'];
    direction?: MenuProps['direction'];
    appearance?: MenuContentProps['appearance'];
    width?: MenuContentProps['width'];
    triggerClassName?: string;
    menuClassName?: string;
    groupHeaderClassName?: string;
    /** The radio mark beside each option. Off, the current choice is the highlighted row. */
    indicators?: boolean;
    /** Solid accent row for the current choice. The default is the faint accent wash. */
    solid?: boolean;
    trackingId?: string;
    'aria-label'?: string;
    /** A visible label beside the menu, which then names the trigger with the value. */
    'aria-labelledby'?: string;
}

/** One choice from a short list, on a button that shows the current choice. */
export default function SelectMenu<T extends string | number>(props: SelectMenuProps<T>) {
    const {
        value,
        onChange,
        options = [],
        groups,
        label,
        renderValue,
        icon,
        caret = true,
        variant = 'secondary',
        size,
        fullWidth,
        disabled = false,
        align,
        offset,
        direction,
        appearance,
        width,
        triggerClassName,
        menuClassName,
        groupHeaderClassName,
        indicators = true,
        solid = false,
        trackingId,
    } = props;

    // React's ids hold colons, which jsdom's selector engine cannot match in tests.
    const valueId = useId().replaceAll(':', '');
    const all = groups ? groups.flatMap((group) => group.options) : options;
    const current =
        all.find((option) => option.value === value) ?? all.find((option) => !option.disabled);
    const text = renderValue
        ? renderValue(current)
        : label !== undefined
          ? `${label}: ${current?.label ?? ''}`
          : (current?.label ?? '');

    const renderOption = (option: SelectMenuOption<T>) => (
        <MenuRadioItem
            key={option.value}
            checked={option.value === value}
            disabled={option.disabled}
            indicator={indicators}
            solid={solid}
            title={option.disabledReason}
            aria-label={
                option.disabled && option.disabledReason
                    ? `${option.label}. ${option.disabledReason}`
                    : undefined
            }
            onSelect={() => {
                if (!option.disabled) onChange(option.value);
            }}>
            {option.label}
        </MenuRadioItem>
    );

    return (
        <Menu disabled={disabled} direction={direction}>
            <MenuTrigger
                variant={variant}
                size={size}
                icon={icon}
                fullWidth={fullWidth}
                disabled={disabled}
                className={triggerClassName}
                trackingId={trackingId}
                aria-label={props['aria-label']}
                aria-labelledby={
                    props['aria-labelledby'] === undefined
                        ? undefined
                        : `${props['aria-labelledby']} ${valueId}`
                }
                caret={caret}>
                <span id={valueId} className={styles.value}>
                    {text}
                </span>
            </MenuTrigger>
            <MenuContent
                align={align}
                offset={offset}
                appearance={appearance}
                width={width}
                className={menuClassName}>
                {groups
                    ? groups.map((group) => (
                          <Fragment key={group.label}>
                              <MenuHeader className={classNames(groupHeaderClassName)}>
                                  {group.label}
                              </MenuHeader>
                              {group.options.map(renderOption)}
                          </Fragment>
                      ))
                    : options.map(renderOption)}
            </MenuContent>
        </Menu>
    );
}

export { SelectMenu };
